import { Component, ComponentWillLoad, h, Host, JSX, Listen, Prop, State } from '@stencil/core';
import { debounce } from 'src/utils/debounce';
import { SearchDocument, SearchQuery, SearchQueryVariables } from 'src/generated/operations';
import { store } from 'src/utils/store';
import { MESSAGES, PATH } from 'src/components/constants';
import { getCompany } from 'src/utils/getCompany';
import { Query, QueryResult } from 'src/utils';
import { removeDuplicateFragments } from 'src/utils/de-dupe-fragments';
import { detectModule } from 'src/utils/detectModule';
import { injectHistory, RouterHistory } from '@stencil/router';
import { getDefaultLocale } from 'src/utils/getDefaultLocale';
import { createFilterForGlobalSearch } from 'src/utils/createFilterForGlobalSearch';
import { OcrError } from 'src/types/OcrError';
import { addClientAlwaysDirective } from 'src/utils/addClientAlwaysDirective';

@Component({
  tag: 'app-search',
  styleUrl: 'app-search.scss',
  shadow: false,
})
export class AppSearch implements ComponentWillLoad {
  @Prop() history: RouterHistory;
  @Prop() public delay = 300;
  @State() private variables!: SearchQueryVariables;
  @State() private placeholderText: string = MESSAGES.SEARCH;
  @State() private isInputFocused = false;
  @State() private isVisible = false;
  @State() private ocrResults: OcrError = { keywords: [], payload: '' };
  @State() private query: string = '';
  @State() private selectedCompany: string = '';
  private inputElement!: HTMLInputElement;
  private dropDownElement!: HTMLDivElement;
  private firstLinkInResults: string | null = null;

  private search!: ((query: string) => Promise<Promise<void>>);

  public focusIn(): void {
    this.isInputFocused = true;
    this.isVisible = true;
  }

  public focusOut(): void {
    this.isInputFocused = false;
  }

  public enter({target, key}: { target: HTMLInputElement; key: string; }): void {
    this.search(target.value).catch((e) => console.log(e));
    if (key === 'Enter') {
      this.isInputFocused = false;
      this.inputElement?.blur();
    }
  }

  @Listen('click', {target: 'document'})
  public watchClickOutside(e: any): void {
    const [target] = e.composedPath() || [];
    if (this.isInputFocused && target === this.inputElement) {
      return;
    }

    const clickInInput = target === this.inputElement;
    const clickInTarget = (this.dropDownElement !== undefined && this.dropDownElement.contains(target))
      || target === this.dropDownElement;
    if (!clickInInput && !clickInTarget) {
      this.isVisible = false;
      this.ocrResults = { keywords: [], payload: '' };
    }
  }

  @Listen('belimoOcrError', {target: 'document'})
  public watchForOcr({detail}: { detail: OcrError }): void {
    this.isVisible = true;
    this.ocrResults = detail;
    this.selectedCompany = '';
    this.query = '';
    this.variables = {
      ...this.variables,
      filter: createFilterForGlobalSearch(getDefaultLocale(store.state.currentRegion), ''),
    }
  }

  public async componentWillLoad(): Promise<void> {
    const search = async (query: string): Promise<void> => {
      this.isVisible = true;
      this.query = query;
      this.variables = {
        ...this.variables,
        filter: createFilterForGlobalSearch(getDefaultLocale(store.state.currentRegion), query, this.selectedCompany)
      };
    };
    this.search = debounce<typeof search>(search, this.delay);
    this.variables = {
      shoppingCenter: store.state.shoppingCenter,
      region: store.state.currentRegion,
      locale: store.state.currentLocale,
      filter: createFilterForGlobalSearch(getDefaultLocale(store.state.currentRegion), this.query, this.selectedCompany),
      limit: 10
    }
    store.onChange('currentLocale', (locale) => this.variables = {
      ...this.variables,
      locale,
    });
    store.onChange('currentRegion', (region) => this.variables = {
      ...this.variables,
      region,
    });
    store.onChange('shoppingCenter', (shoppingCenter) => this.variables = {
      ...this.variables,
      shoppingCenter,
    });
    store.onChange('config', (config) => this.placeholderText = config.i18n.SEARCH);
    const config = store.get('config');
    if (config) {
      this.placeholderText = config.i18n.SEARCH;
    }
  }

  public render(): JSX.Element {
    return (
      <Host class="label-search">

        <div class="label-search__form" >
          {/* Start Manufactory Dropdown*/}
{/*          <company-dropdown onBelimoCompanyFilterChanged={({ detail }) => {
            this.selectedCompany = detail;
            this.variables = {
              ...this.variables,
              filter: createFilterForGlobalSearch(
                getDefaultLocale(store.state.currentRegion),
                this.query,
                detail
              )
            };
          }} />*/}
          {/* End Manufactory Dropdown*/}
          <div class="label-search__input-block" ref={(el) => {
            this.dropDownElement = el;
          }}>
          <div class="dropdown dropdown--fluid">
            <div class="textInput textInput--bg textInput--fluid textInput--icon-left">
              <div class="textInput__icon">
                <belimo-svg-sprite svgId={'search'}/>
              </div>
              <div class="textInput__box">
                <input
                  ref={(el) => {
                    this.inputElement = el;
                  }}
                  onFocusin={() => this.focusIn()}
                  onFocusout={() => this.focusOut()}
                  onKeyUp={(e: any) => this.enter(e)}
                  placeholder={this.placeholderText}
                />
              </div>
            </div>
            {this.isVisible && (this.query.length >= 3 || this.ocrResults.keywords.length > 0 || this.selectedCompany.length > 0) &&
              <div class="dropdown__menu">
                <div class="dropdown__content">
                  {this.ocrResults.keywords.length > 0 &&
                    <div class="label-search__error">
                      <h3>
                        <belimo-i18n messageId={MESSAGES.WE_RE_SORRY}/>
                      </h3>
                      <belimo-i18n messageId={MESSAGES.OCR_CAN_NOT_PROVIDE_RESULTS}/>
                      <request-form-link class="label-search__request-link" file={this.ocrResults.payload}/>
                      <br/>
                      <div class="label-search__ocr_labels">
                        <belimo-i18n messageId={MESSAGES.FOUND_OCR_LABELS}/>
                        :<br/>
                        <strong>{this.ocrResults.keywords.join(', ')}</strong>
                      </div>
                    </div>}
                  {(this.query.length >= 3 || this.selectedCompany.length > 0) &&
                    <Query query={store.state.enableOffline ? addClientAlwaysDirective(removeDuplicateFragments(SearchDocument)) : removeDuplicateFragments(SearchDocument)} variables={this.variables}>
                      {({data, loading}: QueryResult<SearchQuery, SearchQueryVariables>) => {
                        this.firstLinkInResults = null;
                        if (loading) {
                          return (
                            <div class="label-search__loading">
                              <div class="placeholder-item placeholder-item--line placeholder-item--short"/>
                              <div class="placeholder-item placeholder-item--line"/>
                              <div class="placeholder-item placeholder-item--line"/>
                            </div>
                          );
                        }
                        const {autocomplete} = data;
                        if (autocomplete.length === 0) {
                          return (
                            <div class="label-search__error">
                              <h3>
                                <belimo-i18n messageId={MESSAGES.WE_RE_SORRY}/>
                              </h3>
                              <belimo-i18n messageId={MESSAGES.WE_WERE_NOT_ABLE_TO_FIND_MATCH}/>
                              <request-form-link/>
                            </div>
                          );
                        }
                        return (
                          <ul>
                            {autocomplete.map(({
                                                 ID,
                                                 Label,
                                                 company,
                                                 section,
                                                 productType,
                                                 category,
                                               }) => {
                              const module = detectModule(section, productType, category);
                              if (module === null) {
                                return <div>{Label} unknown module</div>
                              }
                              const url = `${store.state.root}${PATH.DETAILS}/${module}/${ID}/${encodeURIComponent(Label)}`;
                              if (this.firstLinkInResults === null) {
                                this.firstLinkInResults = url;
                              }
                              return (
                                <li class="label-search__autocomplete-item">
                                  <stencil-route-link url={url} onClick={() => {
                                    this.trackGa();
                                  }}>
                                    <div class="label-search__autocomplete-core">
                                      <div class="label-search__autocomplete-name">{Label}</div>
                                      <span
                                        class="label-search__autocomplete-company"
                                        innerHTML={getCompany(company)}/>
                                    </div>
                                    <span class="label-search__autocomplete-type">
                                <belimo-i18n messageId={module}/>
                              </span>
                                  </stencil-route-link>
                                </li>
                              );
                            })}
                          </ul>
                        )
                      }}
                    </Query>}
                </div>
              </div>}
          </div>
          <button
            class="btn btn--primary btn--bg"
            type="button"
            onClick={() => {
              if (this.firstLinkInResults === null) {
                return;
              }
              this.trackGa();
              this.history.push({
                ...this.history.location,
                pathname: this.firstLinkInResults,
              });
            }}
          >
            <div class="btn__content">
              <belimo-i18n messageId={MESSAGES.SEARCH}/>
            </div>
          </button>
          </div>
        </div>
        <app-ocr class="label-search__scan"/>
      </Host>
    )
  }

  private trackGa() {
    if (this.query.length === 0) {
      return;
    }
    store.state.gaPush({
      event: 'ga4_event',
      event_name: 'retrofit_tool_global_search',
      retrofit_tool_global_search: {
        search_term: this.query,
        eventCategory: 'retrofit tool',
        eventAction: 'global search',
        eventLabel: this.query
      },
    });
  }
}

injectHistory(AppSearch);
